import { FolderOutlined } from "@ant-design/icons"
import { Col, Flex, Image, Row, Select, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker.tsx"
import { ArchitectNameField } from "../../../../components/Shared/ArchitectNameField.tsx"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { pickableOptions } from "../../../../utils/select/managed-list-options.ts"
import {
  LEAD_SOURCE_NONE,
  withNoneSourceOption
} from "../../../Leads/constants/lead-source-none.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import {
  architectNameClearPatch,
  isLeadSourceArchitect
} from "../../constants/lead-source-architect.ts"
import { type Lead, type LeadAttachment } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"
import { typeLabel } from "../../../Project/helpers/repeated-project-type.ts"
import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif"]

interface ProjectCardValues {
  lead_name: string | null
  project_type: string[]
  source: string | null
  architect_name: string | null
  scope: string | null
  timeline: string | null
  budget_range: string | null
  comments: string | null
  project_creation_date: string | null
}

const propertyImage = (attachments?: LeadAttachment[]): LeadAttachment | null =>
  attachments?.find((attachment) => {
    const extension = attachment?.file_name?.split(".").pop()?.toLowerCase()
    return !!extension && IMAGE_EXTENSIONS.includes(extension)
  }) ?? null

interface LeadOverviewProjectCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadOverviewProjectCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadOverviewProjectCardProps) {
  const { t } = useTranslation()

  const canEditCreationDate = useAppSelector(
    (state) => !!state.auth?.permissions?.codes?.projects?.projects?.project_creation_date_edit
  )

  const sources = useMemo(
    () => withNoneSourceOption(filterOptions?.source),
    [filterOptions?.source]
  )

  // The page swaps `source` for its label before the workspace ever sees it, so
  // the editable value has to be resolved back off the option list.
  const sourceValue = useMemo(
    () =>
      (sources.find((option) => option?.label === lead?.source)?.value as
        | string
        | undefined) ??
      (lead?.source || LEAD_SOURCE_NONE),
    [sources, lead?.source]
  )

  const values: ProjectCardValues = {
    lead_name: lead?.lead_name ?? null,
    project_type: lead?.project_type ?? [],
    source: sourceValue,
    architect_name: lead?.architect_name ?? null,
    scope: lead?.scope ?? null,
    timeline: lead?.timeline ?? null,
    budget_range: lead?.budget_range ?? null,
    comments: lead?.comments ?? null,
    project_creation_date: lead?.project_creation_date ?? null
  }

  const typeNames = lead?.project_type_details?.map(typeLabel) ?? []
  const image = propertyImage(lead?.attachments)

  const labelOf = (
    options: LeadsFilterOptions[keyof LeadsFilterOptions],
    value?: string | null
  ) =>
    (options?.find((option) => option?.value === value)?.label as
      | string
      | undefined) ?? value

  return (
    <WorkspaceCard<ProjectCardValues>
      cardId={"project"}
      title={
        <OverviewCardTitle icon={<FolderOutlined />} accent={"orange"}>
          {t("leadWorkspace.cards.project.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.project.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={onPatch}
      renderEdit={({ values, setValues, saving }) => (
        <Row gutter={[12, 0]}>
          <Col xs={24}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.name")}>
              <TextInput
                autoFocus
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.name")}
                value={values.lead_name ?? ""}
                onChange={(event) =>
                  setValues({ lead_name: event.target.value || null })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.types")}>
              <Select
                mode={"multiple"}
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.types")}
                optionFilterProp={"label"}
                options={filterOptions?.project_type ?? []}
                value={values.project_type}
                onChange={(next) => setValues({ project_type: next })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.source")}>
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.source")}
                optionFilterProp={"label"}
                options={pickableOptions(sources, values.source)}
                value={values.source ?? undefined}
                onChange={(next) =>
                  setValues({
                    source: next ?? null,
                    ...architectNameClearPatch(next)
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          {isLeadSourceArchitect(values.source) && (
            <Col xs={24} sm={12}>
              <ArchitectNameField
                disabled={saving}
                value={values.architect_name}
                onChange={(architect_name) => setValues({ architect_name })}
              />
            </Col>
          )}
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWorkspace.cards.project.timeline")}
            >
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.timeline")}
                options={filterOptions?.timeline ?? []}
                value={values.timeline ?? undefined}
                onChange={(next) => setValues({ timeline: next ?? null })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.budget")}>
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.budget")}
                options={filterOptions?.budget_range ?? []}
                value={values.budget_range ?? undefined}
                onChange={(next) => setValues({ budget_range: next ?? null })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.scope")}>
              <TextArea
                rows={3}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.scope")}
                value={values.scope ?? ""}
                onChange={(event) =>
                  setValues({ scope: event.target.value || null })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24}>
            <LeadOverviewField
              label={t("leadWorkspace.cards.project.description")}
            >
              <TextArea
                rows={3}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.description")}
                value={values.comments ?? ""}
                onChange={(event) =>
                  setValues({ comments: event.target.value || null })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWorkspace.cards.project.creationDate")}
            >
              {canEditCreationDate ? (
                <CustomDatePicker
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.project.creationDate")}
                  value={
                    values.project_creation_date
                      ? dayjs(values.project_creation_date)
                      : null
                  }
                  onChange={(date) =>
                    setValues({
                      project_creation_date: date ? date.format("YYYY-MM-DD") : null
                    })
                  }
                  allowClear
                  style={{ width: "100%" }}
                />
              ) : (
                <Typography.Text type={"secondary"}>
                  {values.project_creation_date
                    ? dayjs(values.project_creation_date).format("MMM D, YYYY")
                    : t("leadWorkspace.cards.project.creationDateNotSet")}
                </Typography.Text>
              )}
            </LeadOverviewField>
          </Col>
        </Row>
      )}
    >
      <Flex gap={"middle"} align={"start"}>
        <div className={"flex-1 min-w-0"}>
          <Row gutter={[12, 8]}>
            <Col xs={24}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.name")}
                value={lead?.lead_name}
              />
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.types")}
                value={typeNames}
              >
                <span>
                  {typeNames.map((name) => (
                    <Tag key={name}>{name}</Tag>
                  ))}
                </span>
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.source")}
                value={labelOf(sources, sourceValue)}
              />
            </Col>
            {isLeadSourceArchitect(sourceValue) && (
              <Col xs={24} sm={12}>
                <LeadOverviewField
                  label={t("leadWizard.project.architectName")}
                  value={lead?.architect_name}
                />
              </Col>
            )}
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.timeline")}
                value={labelOf(filterOptions?.timeline, lead?.timeline)}
              />
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.budget")}
                value={labelOf(filterOptions?.budget_range, lead?.budget_range)}
              />
            </Col>
            <Col xs={24}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.scope")}
                value={lead?.scope}
              />
            </Col>
            <Col xs={24}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.description")}
                value={lead?.comments}
              />
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.project.creationDate")}
                value={
                  lead?.project_creation_date
                    ? dayjs(lead.project_creation_date).format("MMM D, YYYY")
                    : undefined
                }
              />
            </Col>
          </Row>
        </div>
        {!!image && (
          <div className={"w-[120px] shrink-0"}>
            <LeadOverviewField
              label={t("leadWorkspace.cards.project.propertyImage")}
            >
              <Image
                src={image.file}
                alt={image.file_name}
                height={120}
                width={120}
                className={"rounded-lg object-cover"}
              />
            </LeadOverviewField>
          </div>
        )}
      </Flex>
    </WorkspaceCard>
  )
}

export { type ProjectCardValues, propertyImage, LeadOverviewProjectCard }
